import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ZONAS = ["CUENCA", "COSTA", "FORÁNEO"];

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const inputToISO = (v) => (v ? new Date(v + "T12:00:00").toISOString() : null);

export default function ClientRegisterModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    nombre_completo: "",
    nss: "",
    curp: "",
    zona: "CUENCA",
    telefono: "",
    email: "",
    direccion: "",
    comentarios: "",
    fecha_registro: todayISO(),
    fecha_ejecucion: todayISO(),
    fecha_cobranza: todayISO(),
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre_completo || !form.nss || !form.curp) {
      toast.error("Nombre, NSS y CURP son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clients", {
        ...form,
        fecha_registro: inputToISO(form.fecha_registro),
        fecha_ejecucion: inputToISO(form.fecha_ejecucion),
        fecha_cobranza: inputToISO(form.fecha_cobranza),
      });
      onCreated?.();
      setForm({
        nombre_completo: "", nss: "", curp: "",
        zona: "CUENCA",
        telefono: "", email: "", direccion: "", comentarios: "",
        fecha_registro: todayISO(), fecha_ejecucion: todayISO(), fecha_cobranza: todayISO(),
      });
    } catch (err) {
      toast.error("No se pudo registrar el cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="register-modal" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Registrar nuevo cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Nombre completo *</Label>
            <Input data-testid="reg-nombre" value={form.nombre_completo} onChange={upd("nombre_completo")} placeholder="Juan Pérez López" />
          </div>
          <div>
            <Label>NSS *</Label>
            <Input data-testid="reg-nss" value={form.nss} onChange={upd("nss")} placeholder="12345678901" className="font-mono" />
          </div>
          <div>
            <Label>CURP *</Label>
            <Input data-testid="reg-curp" value={form.curp} onChange={upd("curp")} placeholder="XXXX000000XXXXXX00" className="font-mono uppercase" />
          </div>
          <div>
            <Label>Zona</Label>
            <Select value={form.zona} onValueChange={(v) => setForm((f) => ({ ...f, zona: v }))}>
              <SelectTrigger data-testid="reg-zona"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ZONAS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input data-testid="reg-telefono" value={form.telefono} onChange={upd("telefono")} placeholder="55 1234 5678" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="reg-email" type="email" value={form.email} onChange={upd("email")} placeholder="cliente@correo.com" />
          </div>
          <div className="sm:col-span-2">
            <Label>Dirección</Label>
            <Input data-testid="reg-direccion" value={form.direccion} onChange={upd("direccion")} placeholder="Calle, Colonia, Ciudad, Estado" />
          </div>
          <div className="sm:col-span-2">
            <Label>Comentarios</Label>
            <Textarea data-testid="reg-comentarios" value={form.comentarios} onChange={upd("comentarios")} rows={3} placeholder="Notas iniciales del cliente…" />
          </div>

          <div className="sm:col-span-2 border-t pt-4">
            <h4 className="text-sm font-semibold text-neutral-700 mb-3 uppercase tracking-wider">Fechas</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Fecha de registro</Label>
                <Input data-testid="reg-fecha-registro" type="date" value={form.fecha_registro} onChange={upd("fecha_registro")} />
              </div>
              <div>
                <Label>Fecha de ejecución *</Label>
                <Input data-testid="reg-fecha-ejecucion" type="date" value={form.fecha_ejecucion} onChange={upd("fecha_ejecucion")} />
              </div>
              <div>
                <Label>Fecha de cobranza *</Label>
                <Input data-testid="reg-fecha-cobranza" type="date" value={form.fecha_cobranza} onChange={upd("fecha_cobranza")} />
              </div>
            </div>
          </div>

          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button data-testid="reg-submit" type="submit" disabled={saving} className="bg-black text-yellow-400 hover:bg-neutral-900">
              {saving ? "Guardando…" : "Registrar cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
